import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import masterData from '../data/masterTest.json';
import Footer from '../components/Footer';
import './TestPage.css';
import { gradeEmail, gradePassage } from '../utils/scoringUtils';
import { trackGA4Event } from '../utils/GA4Analytics';
import { getAuth } from 'firebase/auth';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import EbookRecommendSlide from '../components/EbookRecommendSlide';
import { trackFunnelEvent } from '../utils/AnalyticsService';

export default function TestPage() {
  // Add a class to the body when the component mounts, and remove it when it unmounts
  useEffect(() => {
    document.body.classList.add('test-page-active');
    document.body.classList.add('notranslate');
    return () => {
      document.body.classList.remove('test-page-active');
      document.body.classList.remove('notranslate');
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { testId } = location.state || {};

  useEffect(() => {
    trackGA4Event('instruction_page_view', { test_type: 'reading', test_id: testId || 1 });
  }, [testId]);
  const [currentTest, setCurrentTest] = useState('typing');
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [showInstructions, setShowInstructions] = useState(true); // Toggle instructions
  const [selectedPattern, setSelectedPattern] = useState('bpo'); // Default to 'bpo' for direct test start

  const startNextSection = (currentSec) => {
    if (selectedPattern === 'bpo') {
      if (currentSec === 'typing') {
        startSentenceCompletion();
      } else if (currentSec === 'sentence') {
        startFillInBlanks();
      } else if (currentSec === 'fill') {
        startJumbledWords();
      } else if (currentSec === 'jumbled') {
        startPassageReconstruction();
      } else if (currentSec === 'passage') {
        startEmailWriting();
      }
    } else {
      // VEPT Pattern (skips 'jumbled' for now)
      if (currentSec === 'typing') {
        startSentenceCompletion();
      } else if (currentSec === 'sentence') {
        startFillInBlanks();
      } else if (currentSec === 'fill') {
        startPassageReconstruction();
      } else if (currentSec === 'passage') {
        startEmailWriting();
      }
    }
  };

  const [showEbookRecommend, setShowEbookRecommend] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(true);

  // --- eBook Recommendation Eligibility Check ---
  useEffect(() => {
    const checkEligibility = async () => {
      // 1. Check local storage purchase flag
      if (localStorage.getItem('ebook_purchased') === 'true') {
        setCheckingEligibility(false);
        return;
      }

      // 2. Check dismissed today
      const todayStr = new Date().toDateString();
      if (localStorage.getItem('ebook_recommend_dismissed_date') === todayStr) {
        setCheckingEligibility(false);
        return;
      }

      // 3. Check Firestore for registered user
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.hasPurchasedEbook || userData.hasPaid || userData.paidTests) {
              localStorage.setItem('ebook_purchased', 'true');
              setCheckingEligibility(false);
              return;
            }
          }
        } catch (err) {
          console.error("Error checking user purchase status:", err);
        }
      }

      // Show recommendation slide if eligible
      setShowEbookRecommend(true);
      setCheckingEligibility(false);
    };

    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(() => {
      checkEligibility();
    });
    return () => unsubscribe();
  }, []);


  // Typing test state
  const [typingTimeLeft, setTypingTimeLeft] = useState(60); // 1 minute

  // Typing test state
  const [typingInput, setTypingInput] = useState('');
  const [typingResults, setTypingResults] = useState({ wpm: 0, accuracy: 0 });

  // Sentence completion state
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [sentenceAnswers, setSentenceAnswers] = useState([]);
  const [sentenceTimeLeft, setSentenceTimeLeft] = useState(25); // 25 seconds per question
  const sentenceInputRef = useRef(null);

  // Fill in blanks state
  const [currentFillIndex, setCurrentFillIndex] = useState(0);
  const [fillAnswers, setFillAnswers] = useState([]);
  const [fillTimeLeft, setFillTimeLeft] = useState(30); // 30 seconds per question
  const fillInputRef = useRef(null);

  // Jumbled words state
  const [currentJumbledIndex, setCurrentJumbledIndex] = useState(0);
  const [jumbledAnswers, setJumbledAnswers] = useState([]);
  const [jumbledTimeLeft, setJumbledTimeLeft] = useState(30); // 30 seconds per question
  const jumbledInputRef = useRef(null);

  // Passage reconstruction state
  const [currentPassageIndex, setCurrentPassageIndex] = useState(0);
  const [passageInputs, setPassageInputs] = useState([]);
  const [passageInput, setPassageInput] = useState('');
  const [passageTimeLeft, setPassageTimeLeft] = useState(30); // 30 seconds reading
  const [writingTimeLeft, setWritingTimeLeft] = useState(90); // 90 seconds writing
  const [isReadingPhase, setIsReadingPhase] = useState(true);
  const passageInputRef = useRef(null);

  // Email writing state
  const [emailInput, setEmailInput] = useState('');
  const [emailTimeLeft, setEmailTimeLeft] = useState(540); // 9 minutes
  const emailInputRef = useRef(null);

  // Handle refresh or tab close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (testStarted && !testCompleted) {
        e.preventDefault();
        e.returnValue = 'Your test progress will be lost if you refresh or close this tab. Are you sure you want to proceed?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [testStarted, testCompleted]);

  // Handle back button
  useEffect(() => {
    if (testStarted && !testCompleted) {
      // Push a new history entry to detect back navigation
      window.history.pushState(null, '', window.location.href);

      const handlePopState = (e) => {
        e.preventDefault();
        const confirmLeave = window.confirm('Your test progress will be lost if you go back. Are you sure you want to leave?');
        if (confirmLeave) {
          const auth = getAuth();
          navigate(auth.currentUser ? '/dashboard' : '/');
        } else {
          // Push the state back to prevent navigation
          window.history.pushState(null, '', window.location.href);
        }
      };

      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [testStarted, testCompleted, navigate]);

  // Fetch questions based on testId
  useEffect(() => {
    if (testId) {
      const loadedQuestions = loadTestQuestions(testId);
      setQuestions(loadedQuestions);
      console.log('Loaded questions:', loadedQuestions); // Debug: Verify question data

      // Initialize answer arrays based on question counts
      setSentenceAnswers(Array(10).fill('')); // 10 sentence completion questions
      setFillAnswers(Array(10).fill('')); // 10 fill in the blanks questions
      setJumbledAnswers(Array(15).fill('')); // 15 jumbled words questions
      setPassageInputs(Array(3).fill('')); // 3 passage reconstruction tasks
    }
  }, [testId]);


  const loadTestQuestions = (testId) => {
    const testIndexStr = testId ? testId.toString() : "1";
    const testData = masterData[testIndexStr] || masterData["1"] || {};

    const typing = testData.typing || [];
    const sentence = testData.sentenceCompletion || [];
    const fill = testData.fillBlanks || [];
    const jumbled = testData.jumbledWords || [];
    const passage = testData.passageReconstruction || [];
    const email = testData.emailWriting || [];

    const questions = [];

    // 1. Typing (1 question) - Index 0
    if (typing.length > 0) {
      questions.push(typing[0]);
    }

    // 2. Sentence Completion (10 questions) - Indices 1-10
    questions.push(...sentence);

    // 3. Fill in Blanks (10 questions) - Indices 11-20
    questions.push(...fill);

    // 4. Jumbled Words (15 questions) - Indices 21-35
    questions.push(...jumbled);

    // 5. Passage Reconstruction (3 questions) - Indices 36-38
    questions.push(...passage);

    // 6. Email Writing (1 question) - Index 39
    if (email.length > 0) {
      questions.push(email[0]);
    }

    return questions;
  };

  const activeAudioRef = useRef(null);

  const stopAllSpeech = () => {
    if (activeAudioRef.current) {
      activeAudioRef.current.pause();
      activeAudioRef.current.onended = null;
      activeAudioRef.current.onerror = null;
      activeAudioRef.current = null;
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  };

  const speakInstructionSynth = (text, onEnd) => {
    if (!text) { if (onEnd) onEnd(); return; }
    stopAllSpeech();
    if ('speechSynthesis' in window) {
      setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        utterance.rate = 0.9;
        utterance.pitch = 1.0;
        
        const voices = window.speechSynthesis.getVoices();
        const preferredVoice = voices.find(voice =>
          (voice.name.includes('Google') && voice.lang === 'en-US') ||
          (voice.name.includes('Samantha') && voice.lang === 'en-US') ||
          (voice.name.includes('Natural') && voice.lang === 'en-US')
        );

        if (preferredVoice) {
          utterance.voice = preferredVoice;
        }

        utterance.onend = () => { if (onEnd) onEnd(); };
        utterance.onerror = () => { if (onEnd) onEnd(); };

        window.speechSynthesis.speak(utterance);
      }, 50);
    } else {
      if (onEnd) onEnd();
    }
  };

  const playAudioOrSpeak = (audioUrl, fallbackText, onEnd) => {
    stopAllSpeech();
    if (!audioUrl) {
      speakInstructionSynth(fallbackText, onEnd);
      return;
    }

    const audio = new Audio(audioUrl);
    activeAudioRef.current = audio;

    audio.onended = () => {
      activeAudioRef.current = null;
      if (onEnd) onEnd();
    };

    audio.onerror = (e) => {
      console.warn(`[Audio Player] MP3 failed at ${audioUrl}, falling back:`, e);
      activeAudioRef.current = null;
      speakInstructionSynth(fallbackText, onEnd);
    };

    audio.play().catch(e => {
      console.warn(`[Audio Player] Autoplay prevented for ${audioUrl}, falling back:`, e);
      activeAudioRef.current = null;
      speakInstructionSynth(fallbackText, onEnd);
    });
  };

  const speakInstruction = (text) => {
    speakInstructionSynth(text);
  };

  // Handle Start Section button
  const handleStartSection = () => {
    window.speechSynthesis?.cancel(); // Stop speech
    setShowInstructions(false);

    // Track section started
    let trackerName = currentTest;
    if (currentTest === 'typing') trackerName = 'dictation';
    else if (currentTest === 'sentence') trackerName = 'dictation';
    else if (currentTest === 'fill') trackerName = 'reading';
    else if (currentTest === 'jumbled') trackerName = 'reading';
    else if (currentTest === 'passage') trackerName = 'writing';
    else if (currentTest === 'email') trackerName = 'writing';

    trackFunnelEvent(`${trackerName}_started`);
  };

  // Define handleNextAction before useEffect
  const handleNextAction = () => {
    switch (currentTest) {
      case 'typing':
        if (typingTimeLeft > 0) return;
        evaluateTypingTest();
        break;
      case 'sentence':
        if (!sentenceAnswers[currentSentenceIndex]) return;
        handleNextSentence();
        break;
      case 'fill':
        if (!fillAnswers[currentFillIndex]) return;
        handleNextFill();
        break;
      case 'jumbled':
        if (!jumbledAnswers[currentJumbledIndex]) return;
        handleNextJumbled();
        break;
      case 'passage':
        if (isReadingPhase) {
          setIsReadingPhase(false);
        } else {
          if (!passageInput) return;
          handleNextPassage();
        }
        break;
      case 'email':
        if (!emailInput) return;
        evaluateEmailWriting();
        break;
      default:
        break;
    }
  };

  // Handle Enter key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Do NOT intercept Enter key in Email Writing section so candidates can type new lines/paragraphs
      if (e.key === 'Enter' && currentTest !== 'email') {
        e.preventDefault();
        handleNextAction();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    handleNextAction,
    currentTest,
    typingTimeLeft,
    sentenceAnswers,
    currentSentenceIndex,
    fillAnswers,
    currentFillIndex,
    jumbledAnswers,
    currentJumbledIndex,
    isReadingPhase,
    passageInput,
    emailInput,
  ]);

  // Start the test
  const startTest = () => {
    trackGA4Event('test_started', { test_type: 'reading', test_id: testId || 1 });
    trackFunnelEvent('test_started', { test_type: 'reading', test_id: testId || 1 });
    setTestStarted(true);
    setCurrentTest('typing');
    setShowInstructions(true); // Show typing instructions first
  };

  // Typing test functions
  const evaluateTypingTest = () => {
    const typingQuestion = questions[0];
    if (!typingInput.trim()) {
      console.log('Typing evaluation: Empty input, WPM: 0, Accuracy: 0');
      setTypingResults({ wpm: 0, accuracy: 0, rightWords: 0, wrongWords: 0, keystrokes: 0, typingInput: '', paragraph: typingQuestion.paragraph });
      startNextSection('typing');
      return;
    }
    const cleanRef = typingQuestion.paragraph.trim();
    const cleanUser = typingInput.trim();
    
    const refWords = cleanRef.split(/\s+/).filter(w => w.length > 0);
    const userWords = cleanUser.split(/\s+/).filter(w => w.length > 0);
    
    // LCS (Longest Common Subsequence) to match words correctly preventing index-shifting penalty
    const m = refWords.length;
    const n = userWords.length;
    const dp = Array(m + 1).fill().map(() => Array(n + 1).fill(0));
    
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const wordA = refWords[i - 1].toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
        const wordB = userWords[j - 1].toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
        if (wordA === wordB) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }
    
    const rightWords = dp[m][n];
    const wrongWords = Math.max(0, userWords.length - rightWords);
    const keystrokes = typingInput.length;
    
    // Time Taken (max 60s, min 30s for speed check)
    const timeInSeconds = Math.max(60 - typingTimeLeft, 30);
    const timeInMinutes = timeInSeconds / 60;
    
    // Standard WPM = (Total Typed Chars / 5) / Time (min)
    const grossWpm = timeInMinutes > 0 ? Math.round((keystrokes / 5) / timeInMinutes) : 0;
    const wpm = Math.max(0, Math.round(grossWpm - (wrongWords / timeInMinutes)));
    
    // Accuracy
    const accuracy = userWords.length > 0 ? Math.min(100, Math.round((rightWords / userWords.length) * 100)) : 0;
    
    console.log(`Typing evaluation: WPM: ${wpm}, Accuracy: ${accuracy}%, Right: ${rightWords}, Wrong: ${wrongWords}, Keystrokes: ${keystrokes}`);
    
    setTypingResults({
      wpm,
      accuracy,
      rightWords,
      wrongWords,
      keystrokes,
      typingInput: cleanUser,
      paragraph: cleanRef
    });
    startNextSection('typing');
  };

  // Sentence completion functions
  const startSentenceCompletion = () => {
    trackFunnelEvent('dictation_completed');
    setCurrentSentenceIndex(0);
    setSentenceTimeLeft(25);
    setCurrentTest('sentence');
    setShowInstructions(true); // Show instructions before starting
  };

  const handleSentenceAnswer = (e) => {
    const newAnswers = [...sentenceAnswers];
    newAnswers[currentSentenceIndex] = e.target.value;
    setSentenceAnswers(newAnswers);
  };

  const handleNextSentence = () => {
    if (currentSentenceIndex < 9) {
      // 10 questions (0-9)
      setCurrentSentenceIndex((prev) => prev + 1);
      setSentenceTimeLeft(25);
    } else {
      startNextSection('sentence');
    }
  };

  // Auto-focus for sentence completion input
  useEffect(() => {
    if (currentTest === 'sentence' && sentenceInputRef.current && !showInstructions) {
      sentenceInputRef.current.focus();
    }
  }, [currentTest, currentSentenceIndex, showInstructions]);

  // Fill in blanks functions
  const startFillInBlanks = () => {
    trackFunnelEvent('dictation_completed');
    setCurrentFillIndex(0);
    setFillTimeLeft(30);
    setCurrentTest('fill');
    setShowInstructions(true); // Show instructions before starting
  };

  const handleFillAnswer = (value) => {
    const newAnswers = [...fillAnswers];
    newAnswers[currentFillIndex] = value;
    setFillAnswers(newAnswers);
  };

  const handleNextFill = () => {
    if (currentFillIndex < 9) {
      // 10 questions (0-9)
      setCurrentFillIndex((prev) => prev + 1);
      setFillTimeLeft(30);
    } else {
      startNextSection('fill');
    }
  };

  // Auto-focus for fill in the blanks (first radio button)
  useEffect(() => {
    if (currentTest === 'fill' && fillInputRef.current && !showInstructions) {
      fillInputRef.current.focus();
    }
  }, [currentTest, currentFillIndex, showInstructions]);

  // Jumbled words functions
  const startJumbledWords = () => {
    trackFunnelEvent('reading_completed');
    setCurrentJumbledIndex(0);
    setJumbledTimeLeft(30);
    setCurrentTest('jumbled');
    setShowInstructions(true); // Show instructions before starting
  };

  const handleJumbledAnswer = (e) => {
    const newAnswers = [...jumbledAnswers];
    newAnswers[currentJumbledIndex] = e.target.value;
    setJumbledAnswers(newAnswers);
  };

  const handleNextJumbled = () => {
    if (currentJumbledIndex < 14) {
      // 15 questions (0-14)
      setCurrentJumbledIndex((prev) => prev + 1);
      setJumbledTimeLeft(30);
    } else {
      startNextSection('jumbled');
    }
  };

  // Auto-focus for jumbled words input
  useEffect(() => {
    if (currentTest === 'jumbled' && jumbledInputRef.current && !showInstructions) {
      jumbledInputRef.current.focus();
    }
  }, [currentTest, currentJumbledIndex, showInstructions]);

  // Passage reconstruction functions
  const startPassageReconstruction = () => {
    trackFunnelEvent('reading_completed');
    setCurrentPassageIndex(0);
    setPassageInput('');
    setPassageTimeLeft(30);
    setWritingTimeLeft(90);
    setIsReadingPhase(true);
    setCurrentTest('passage');
    setShowInstructions(true); // Show instructions before starting
  };

  const handleNextPassage = () => {
    const newInputs = [...passageInputs];
    newInputs[currentPassageIndex] = passageInput;
    setPassageInputs(newInputs);

    if (currentPassageIndex < 2) {
      // 3 passages (0-2)
      setCurrentPassageIndex((prev) => prev + 1);
      setPassageInput('');
      setPassageTimeLeft(30);
      setWritingTimeLeft(90);
      setIsReadingPhase(true);
    } else {
      startNextSection('passage');
    }
  };

  // Auto-focus for passage reconstruction textarea (after reading phase)
  useEffect(() => {
    if (currentTest === 'passage' && !isReadingPhase && passageInputRef.current && !showInstructions) {
      passageInputRef.current.focus();
    }
  }, [currentTest, isReadingPhase, showInstructions]);

  // Email writing functions
  const startEmailWriting = () => {
    trackFunnelEvent('writing_completed');
    setEmailInput('');
    setEmailTimeLeft(540);
    setCurrentTest('email');
    setShowInstructions(true); // Show instructions before starting
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const evaluateEmailWriting = () => {
    setIsSubmitting(true);
    // 1. Calculate Email Score (0-10) using our new algorithm
    const emailQuestion = questions.find((question) => question.prompt);
    const emailResult = gradeEmail(emailInput, emailQuestion?.requiredThemes || []);
    console.log("Email Grading:", emailResult);

    // 2. Calculate Passage Scores
    // We compare each user input against the original text in masterData
    // We need to look up the original passages again.
    const passageQuestions = questions.filter(q => q.sentences); // Identify passage questions
    const passageResults = passageInputs.map((input, index) => {
      const originalText = passageQuestions[index]?.sentences || "";
      return gradePassage(originalText, input);
    });
    console.log("Passage Grading:", passageResults);

    trackGA4Event('test_completed', { test_type: 'reading', test_id: testId || 1 });
    trackFunnelEvent('writing_completed');
    trackFunnelEvent('test_completed', { test_type: 'reading', test_id: testId || 1 });
    setTestCompleted(true); // Mark test as completed before navigating

    // Simulate AI processing delay
    setTimeout(() => {
      // Navigate to ResultPage.js with test results
      navigate('/result', {
        state: {
          testId,
          typingResults,
          sentenceAnswers,
          fillAnswers,
          jumbledAnswers,
          passageInputs,
          emailInput,
          questions,
          // NEW: Pass the calculated scores
          emailScore: emailResult,
          passageScores: passageResults
        },
      });
    }, 2500);
  };

  // Auto-focus for email writing textarea
  useEffect(() => {
    if (currentTest === 'email' && emailInputRef.current && !showInstructions) {
      emailInputRef.current.focus();
    }
  }, [currentTest, showInstructions]);

  // Dictation for instruction screens
  useEffect(() => {
    if (showInstructions && testStarted) {
      let text = '';
      switch (currentTest) {
        case 'typing':
          text = "Improve your typing speed and accuracy. You will have 1 minute to type the paragraph shown. Click 'Start Section' to begin.";
          break;
        case 'sentence':
          text = "Test your vocabulary and grammar by completing sentences. Type one word that best completes each sentence within 25 seconds. Click 'Start Section' to begin.";
          break;
        case 'fill':
          text = "Strengthen your language skills by filling in missing words. Select the correct word from the multiple-choice options within 30 seconds. Click 'Start Section' to begin.";
          break;
        case 'jumbled':
          text = "Boost logical thinking by unscrambling jumbled sentences. Type the correct sentence by rearranging the words within 30 seconds. Click 'Start Section' to begin.";
          break;
        case 'passage':
          text = "Enhance memory and paraphrasing by reconstructing passages. Read the passage for 30 seconds, then rewrite it in your own words within 90 seconds. Click 'Start Section' to begin.";
          break;
        case 'email':
          text = "Practice professional communication by writing a clear, formal email. Cover every point in the prompt and write at least 100 words within 9 minutes. Click 'Start Section' to begin.";
          break;
        default:
          break;
      }
      if (text) {
        speakInstruction(text);
      }
    }
    return () => window.speechSynthesis?.cancel(); // Cleanup on unmount or state change
  }, [showInstructions, currentTest, testStarted]);

  // Timer effects
  useEffect(() => {
    if (currentTest === 'typing' && typingTimeLeft > 0 && !showInstructions) {
      const timer = setInterval(() => {
        setTypingTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            evaluateTypingTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentTest, typingTimeLeft, showInstructions, evaluateTypingTest]);

  useEffect(() => {
    if (currentTest === 'sentence' && sentenceTimeLeft > 0 && !showInstructions) {
      const timer = setInterval(() => {
        setSentenceTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleNextSentence();
            return 25;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentTest, fillTimeLeft, showInstructions, handleNextFill]);

  useEffect(() => {
    if (currentTest === 'fill' && fillTimeLeft > 0 && !showInstructions) {
      const timer = setInterval(() => {
        setFillTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleNextFill();
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentTest, fillTimeLeft, showInstructions, handleNextFill]);

  useEffect(() => {
    if (currentTest === 'jumbled' && jumbledTimeLeft > 0 && !showInstructions) {
      const timer = setInterval(() => {
        setJumbledTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleNextJumbled();
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentTest, jumbledTimeLeft, showInstructions, handleNextJumbled]);

  useEffect(() => {
    if (currentTest === 'passage' && isReadingPhase && passageTimeLeft > 0 && !showInstructions) {
      const timer = setInterval(() => {
        setPassageTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsReadingPhase(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentTest, passageTimeLeft, isReadingPhase, showInstructions, handleNextPassage]);

  useEffect(() => {
    if (currentTest === 'passage' && !isReadingPhase && writingTimeLeft > 0 && !showInstructions) {
      const timer = setInterval(() => {
        setWritingTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleNextPassage();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentTest, writingTimeLeft, isReadingPhase, showInstructions, handleNextPassage]);

  useEffect(() => {
    if (currentTest === 'email' && emailTimeLeft > 0 && !showInstructions) {
      const timer = setInterval(() => {
        setEmailTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            evaluateEmailWriting();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentTest, emailTimeLeft, showInstructions, evaluateEmailWriting]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleExitTest = () => {
    if (window.confirm('Are you sure you want to exit the test? Your progress will be lost and you cannot resume it.')) {
      const auth = getAuth();
      navigate(auth.currentUser ? '/dashboard' : '/');
    }
  };



  // CHECK: Validation logic moved here to ensure all hooks run first
  if (!testId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="test-container-glass p-8 text-center bg-white rounded-xl shadow-xl">
          <h2 className="test-title text-2xl font-bold text-red-600 mb-4">Invalid Test Selected</h2>
          <p className="test-subtitle text-gray-600 mb-6">Please go back to the dashboard and select a valid test.</p>
          <button
            onClick={() => {
                const auth = getAuth();
                navigate(auth.currentUser ? '/dashboard' : '/');
            }}
            className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (checkingEligibility) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (showEbookRecommend) {
    return (
      <EbookRecommendSlide 
        testType="reading" 
        onContinue={() => setShowEbookRecommend(false)} 
      />
    );
  }

  // Loading Overlay
  if (isSubmitting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 bg-opacity-90 fixed inset-0 z-50">
        <div className="text-center p-8 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-2xl">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-emerald-400 mb-4"></div>
          <h2 className="text-2xl font-bold text-white mb-2">Analyzing Responses...</h2>
          <p className="text-gray-300">Our AI is evaluating your performance.</p>
        </div>
      </div>
    );
  }

  // Reading & Writing Test Instruction Screen (Direct Start)
  if (!testStarted) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass">
            <h2 className="test-title">Versant Writing Test {testId}</h2>
            <div className="text-left test-subtitle space-y-2">
              <p>This test has {selectedPattern === 'bpo' ? 'six' : 'five'} sections:</p>
              <ol className="list-decimal pl-5 space-y-1">
                {selectedPattern === 'bpo' ? (
                  <>
                    <li>Typing Test (1 minute)</li>
                    <li>Sentence Completion (10 questions, about 4 minutes)</li>
                    <li>Fill in the Blanks (10 questions, 5 minutes)</li>
                    <li>Jumbled Words (15 questions, 7.5 minutes)</li>
                    <li>Passage Reconstruction (3 tasks, 6 minutes)</li>
                    <li>Email Writing (9 minutes, at least 100 words)</li>
                  </>
                ) : (
                  <>
                    <li>Typing Test (1 minute)</li>
                    <li>Sentence Completion (10 questions, about 4 minutes)</li>
                    <li>Fill in the Blanks (10 questions, 5 minutes)</li>
                    <li>Passage Reconstruction (3 tasks, 6 minutes)</li>
                    <li>Email Writing (9 minutes, at least 100 words)</li>
                  </>
                )}
              </ol>
            </div>
            <button
              onClick={startTest}
              className="test-btn test-btn-primary"
            >
              Start Test
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Typing test instructions
  if (currentTest === 'typing' && showInstructions) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass max-w-md">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Typing Test</h2>
            <div className="test-question-area">
              <p className="test-subtitle">
                Improve your typing speed and accuracy through audio dictation. Listen to the audio carefully and type the dictated text into the input field. Click ‘Start Section’ to begin.
              </p>
            </div>
            <button
              onClick={handleStartSection}
              className="test-btn test-btn-primary"
            >
              Start Section
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Typing test
  if (currentTest === 'typing') {
    const typingQuestion = questions[0];
    if (!typingQuestion) {
      return (
        <>
          <div className="min-h-screen flex items-center justify-center">
            <p className="text-lg text-gray-600">Loading test...</p>
          </div>
          <Footer />
        </>
      );
    }
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Typing Test</h2>
            <div className="timer-display-circular">
              <div className="timer-text-circular">{formatTime(typingTimeLeft)}</div>
            </div>
            <div className="test-question-area">
              <p>{typingQuestion.paragraph}</p>
            </div>
            <textarea
              value={typingInput}
              onChange={(e) => setTypingInput(e.target.value)}
              className="test-textarea"
              placeholder="Type the text above..."
              disabled={typingTimeLeft <= 0}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-gramm="false"
              data-enable-grammarly="false"
            />
            <button
              onClick={evaluateTypingTest}
              disabled={typingTimeLeft > 0 && typingInput.length === 0}
              className={`test-btn ${typingTimeLeft > 0 ? 'test-btn-secondary' : 'test-btn-primary'}`}>
              {typingTimeLeft > 0 ? 'Press Enter to Skip' : 'Continue'}
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Sentence completion instructions
  if (currentTest === 'sentence' && showInstructions) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass max-w-md">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Sentence Completion</h2>
            <div className="test-question-area">
              <p className="test-subtitle">
                Test your vocabulary and grammar by completing sentences. Type the correct word to fill in each sentence’s blank within 30 seconds. Click ‘Start Section’ to begin.
              </p>
            </div>
            <button onClick={handleStartSection} className="test-btn test-btn-primary">Start Section</button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Sentence completion
  if (currentTest === 'sentence') {
    const currentQuestion = questions[1 + currentSentenceIndex]; // Questions 1-10 are sentence completion
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Sentence Completion</h2>
            <p className="test-subtitle">Question {currentSentenceIndex + 1} of 10</p>
            <div className="timer-display-circular">
              <div className="timer-text-circular">{sentenceTimeLeft}s</div>
            </div>
            <div className="test-question-area">
              <p>{currentQuestion.question}</p>
            </div>
            <input
              ref={sentenceInputRef}
              type="text"
              value={sentenceAnswers[currentSentenceIndex]}
              onChange={handleSentenceAnswer}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && sentenceAnswers[currentSentenceIndex]?.trim()) {
                  handleNextSentence();
                }
              }}
              placeholder="Complete the sentence..."
              className="test-input"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-gramm="false"
              data-enable-grammarly="false"
            />
            <button
              onClick={handleNextSentence}
              disabled={!sentenceAnswers[currentSentenceIndex]}
              className="test-btn test-btn-primary"
            >
              {currentSentenceIndex < 9 ? 'Next (or press Enter)' : 'Continue'}
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Fill in blanks instructions
  if (currentTest === 'fill' && showInstructions) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass max-w-md">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Fill in the Blanks</h2>
            <div className="test-question-area">
              <p className="test-subtitle">
                Strengthen your language skills by filling in missing words. Select the correct word from the multiple-choice options within 30 seconds. Click ‘Start Section’ to begin.
              </p>
            </div>
            <button onClick={handleStartSection} className="test-btn test-btn-primary">Start Section</button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Fill in blanks
  if (currentTest === 'fill') {
    const currentQuestion = questions[11 + currentFillIndex]; // Questions 11-20 are fill in the blanks
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Fill in the Blanks</h2>
            <p className="test-subtitle">Question {currentFillIndex + 1} of 10</p>
            <div className="timer-display-circular">
              <div className="timer-text-circular">{fillTimeLeft}s</div>
            </div>
            <div className="test-question-area">
              <p>{currentQuestion.question}</p>
              <div className="space-y-2 mt-4 text-left">
                {currentQuestion.options.map((option, i) => (
                  <label key={i} className="flex items-center space-x-3 p-2 rounded-md hover:bg-white/20">
                    <input
                      ref={i === 0 ? fillInputRef : null}
                      type="radio"
                      value={option}
                      checked={fillAnswers[currentFillIndex] === option}
                      onChange={() => handleFillAnswer(option)}
                      className="h-4 w-4 text-green-300 bg-transparent border-white/50 focus:ring-green-300"
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
            <button
              onClick={handleNextFill}
              disabled={!fillAnswers[currentFillIndex]}
              className="test-btn test-btn-primary"
            >
              {currentFillIndex < 9 ? 'Next (or press Enter)' : 'Continue'}
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Jumbled words instructions
  if (currentTest === 'jumbled' && showInstructions) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass max-w-md">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Jumbled Words</h2>
            <div className="test-question-area">
              <p className="test-subtitle">
                Boost logical thinking by unscrambling jumbled sentences. Type the correct sentence by rearranging the words within 30 seconds. Click ‘Start Section’ to begin.
              </p>
            </div>
            <button onClick={handleStartSection} className="test-btn test-btn-primary">Start Section</button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Jumbled words
  if (currentTest === 'jumbled') {
    const currentQuestion = questions[21 + currentJumbledIndex]; // Questions 21-35 are jumbled words
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Jumbled Words</h2>
            <p className="test-subtitle">Question {currentJumbledIndex + 1} of 15</p>
            <div className="timer-display-circular">
              <div className="timer-text-circular">{jumbledTimeLeft}s</div>
            </div>
            <div className="test-question-area">
              <p>Unscramble these words to make a correct sentence:</p>
              <p className="text-xl font-bold mt-2">{currentQuestion.jumbled.replace(/\//g, ' ')}</p>
            </div>
            <input
              ref={jumbledInputRef}
              type="text"
              value={jumbledAnswers[currentJumbledIndex]}
              onChange={handleJumbledAnswer}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && jumbledAnswers[currentJumbledIndex]?.trim()) {
                  handleNextJumbled();
                }
              }}
              placeholder="Type the correct sentence..."
              className="test-input"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-gramm="false"
              data-enable-grammarly="false"
            />
            <button
              onClick={handleNextJumbled}
              disabled={!jumbledAnswers[currentJumbledIndex]}
              className="test-btn test-btn-primary"
            >
              {currentJumbledIndex < 14 ? 'Next (or press Enter)' : 'Continue'}
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Passage reconstruction instructions
  if (currentTest === 'passage' && showInstructions) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass max-w-md">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Passage Reconstruction</h2>
            <div className="test-question-area">
              <p className="test-subtitle">
                Enhance memory and paraphrasing by reconstructing passages. Read the passage for 30 seconds, then rewrite it in your own words within 90 seconds. Click ‘Start Section’ to begin.
              </p>
            </div>
            <button onClick={handleStartSection} className="test-btn test-btn-primary">Start Section</button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Passage reconstruction
  if (currentTest === 'passage') {
    const currentPassage = questions[36 + currentPassageIndex]; // Questions 36-38 are passage reconstruction
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Passage Reconstruction</h2>
            <p className="test-subtitle">Passage {currentPassageIndex + 1} of 3</p>
            <div className="timer-display-circular">
              <div className="timer-text-circular">
                {isReadingPhase ? formatTime(passageTimeLeft) : formatTime(writingTimeLeft)}
              </div>
            </div>
            {isReadingPhase ? (
              <>
                <div className="test-question-area">
                  <p>Read this passage carefully. It will disappear in {passageTimeLeft} seconds.</p>
                  <p className="mt-4">{currentPassage.sentences}</p>
                </div>
                <button onClick={() => setIsReadingPhase(false)} className="test-btn test-btn-primary">
                  I'm Ready to Write (or press Enter)
                </button>
              </>
            ) : (
              <>
                <div className="test-question-area">
                  <p>Reconstruct the passage in your own words:</p>
                </div>
                <textarea
                  ref={passageInputRef}
                  value={passageInput}
                  onChange={(e) => setPassageInput(e.target.value)}
                  placeholder="Write the passage here..."
                  className="test-textarea min-h-[200px]"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-gramm="false"
                  data-enable-grammarly="false"
                />
                <button onClick={handleNextPassage} disabled={!passageInput} className="test-btn test-btn-primary">
                  {currentPassageIndex < 2 ? 'Next Passage (or press Enter)' : 'Continue'}
                </button>
              </>
            )}
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Email writing instructions
  if (currentTest === 'email' && showInstructions) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass max-w-md">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Email Writing</h2>
            <div className="test-question-area">
              <p className="test-subtitle">
                Practice professional communication by writing a clear, formal email. Cover every point in the prompt and write at least 100 words within 9 minutes. Click ‘Start Section’ to begin.
              </p>
            </div>
            <button onClick={handleStartSection} className="test-btn test-btn-primary">Start Section</button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Email writing
  if (currentTest === 'email') {
    const emailQuestion = questions[39]; // Question 39 is email writing
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="test-container-glass">
            <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">Email Writing</h2>
            <div className="timer-display-circular">
              <div className="timer-text-circular">{formatTime(emailTimeLeft)}</div>
            </div>
            <div className="test-question-area">
              <p>{emailQuestion.prompt}</p>
            </div>
            <textarea
              ref={emailInputRef}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="Write your email here..."
              className="test-textarea min-h-[200px]"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-gramm="false"
              data-enable-grammarly="false"
            />
            <p className="test-subtitle mt-2">Word count: {emailInput.trim() ? emailInput.trim().split(/\s+/).length : 0} / 100 minimum</p>
            <button
              onClick={evaluateEmailWriting}
              disabled={!emailInput}
              className="test-btn test-btn-primary"
            >
              {emailTimeLeft > 0 ? 'Submit Email' : 'Finish Test'}
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // If test is completed, redirect happened, so nothing to render here
  return <Footer />;
}
